// The Picker's rules as pure functions (no imports), so the form choice and its wording can be checked with plain node.

export type PickerOption = { value: unknown; label: string; group?: string };

/** Pills while everything fits on two lines (at most 7 options, every label at most 20 characters), otherwise the field. */
export function pickerMode(options: readonly { label: string }[]): 'pills' | 'field' {
  return options.length <= 7 && options.every(o => o.label.length <= 20) ? 'pills' : 'field';
}

/** The chosen values as an array; the "all, also new" value (and nothing) is empty. */
export function selectedValues(selected: unknown, allValue: unknown = null): unknown[] {
  if (selected === allValue || selected == null) return [];
  return Array.isArray(selected) ? selected : [selected];
}

/** The chosen options in the order of the list: the first `max` to name in the field, plus how many more. */
export function shownNames<T extends PickerOption>(selected: unknown, options: readonly T[], { allValue = null as unknown, max = 3 } = {}) {
  const values = selectedValues(selected, allValue);
  const chosen = options.filter(o => values.includes(o.value));
  return { shown: chosen.slice(0, max), more: Math.max(0, chosen.length - max) };
}

export type PickerSummary =
  | { kind: 'all_new' } | { kind: 'none' } | { kind: 'all_now'; n: number } | { kind: 'all'; n: number }
  | { kind: 'except'; group: string } | { kind: 'some'; n: number; of: number };

/** What the line under the field says, as data (the component words it): "Alle ticketsoorten, ook nieuwe", "Alles behalve extra's", "6 van 14 ticketsoorten". */
export function pickerSummary(selected: unknown, options: readonly PickerOption[],
  { allNew = false, allValue = null as unknown } = {}): PickerSummary {
  if (allNew && selected === allValue) return { kind: 'all_new' };
  const values = selectedValues(selected, allValue);
  const n = options.filter(o => values.includes(o.value)).length;
  if (!n) return { kind: 'none' };
  if (n === options.length) return allNew ? { kind: 'all_now', n } : { kind: 'all', n };
  // Everything except one whole group reads as such, when there is more than one group
  const groups = [...new Set(options.map(o => o.group))];
  if (groups.length > 1) {
    const left = groups.filter(g => options.some(o => o.group === g && !values.includes(o.value)));
    const [g] = left;
    if (left.length === 1 && g && options.every(o => o.group !== g || !values.includes(o.value)))
      return { kind: 'except', group: g };
  }
  return { kind: 'some', n, of: options.length };
}

// The Picker's look, shared with the pickers built beside it (VisitDateFilter, SegmentBuilder) so a row reads as one
export const FOCUS_RING = 'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink dark:focus-visible:outline-surface-0';
/** A small quick choice in a panel */
export const CHIP = `h-7 px-2.5 rounded-full text-xs font-semibold cursor-pointer border-0 ${FOCUS_RING}`;
export const CHIP_ON = 'bg-ink text-white dark:bg-surface-0 dark:text-ink';
export const CHIP_OFF = 'bg-ash text-ink dark:bg-surface-800 dark:text-surface-0';
/** A segmented switch of what a list shows */
export const TAB = `h-7 px-3 rounded-full text-xs font-semibold cursor-pointer border-0 ${FOCUS_RING}`;
export const TAB_ON = 'bg-surface-0 text-ink shadow-sm dark:bg-surface-600 dark:text-surface-0';
export const TAB_OFF = 'bg-transparent text-surface-600 dark:text-surface-300';
/** A chosen pill */
export const PILL_ON = 'bg-ink text-white border-ink dark:bg-surface-0 dark:text-ink dark:border-surface-0';
/** The trigger of a filter in a filter row: dashed while empty, solid once set */
export const filterPill = (set: boolean) => ['inline-flex items-center gap-1.5 h-[2.2rem] min-w-0 max-w-[16rem] pl-3 pr-8 text-left rounded-full border bg-surface-0 dark:bg-surface-950 text-ink dark:text-surface-0 text-sm',
  set ? 'border-solid border-surface-400 dark:border-surface-500' : 'border-dashed border-surface-300 dark:border-surface-600'];
